/* =============================================================
   TOUR SPRAY — Landing motion orchestrator
   - Lenis smooth scroll (desktop, no reduced-motion)
   - GSAP ScrollTrigger (parallax, pinned before/after, reveals)
   - Vanilla helpers (counters, magnetic, kit drag, add-to-cart)
   ============================================================= */

(function () {
  'use strict';

  const root = document.querySelector('[data-ts-root]');
  if (!root) return;

  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;
  const hasGSAP = typeof window.gsap !== 'undefined';
  const hasST = hasGSAP && typeof window.ScrollTrigger !== 'undefined';

  if (hasST) window.gsap.registerPlugin(window.ScrollTrigger);

  /* ---------- 1. Split headlines into lines ---------- */
  function splitLines() {
    const nodes = root.querySelectorAll('[data-ts-split-lines]');
    nodes.forEach((el) => {
      if (el.dataset.tsSplitDone) return;
      const raw = el.innerHTML.trim();
      const parts = raw.split(/\n|<br\s*\/?>/i).map((s) => s.trim()).filter(Boolean);
      el.innerHTML = parts
        .map((line, i) => `<span class="tsv2-line"><span style="--i:${i}">${line}</span></span>`)
        .join('');
      el.classList.add('tsv2-lines');
      el.dataset.tsSplitDone = '1';
    });
  }

  /* ---------- 2. Intersection reveals (fallback + always-on) ---------- */
  function setupReveals() {
    const targets = root.querySelectorAll('.tsv2-reveal, .tsv2-reveal-stagger, .tsv2-lines');
    if (!('IntersectionObserver' in window)) {
      targets.forEach((t) => t.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    targets.forEach((t) => io.observe(t));
  }

  /* ---------- 3. Hero parallax ---------- */
  function setupParallax() {
    if (prefersReduced || !hasST) return;
    const layers = root.querySelectorAll('[data-ts-parallax]');
    layers.forEach((layer) => {
      const speed = parseFloat(layer.dataset.tsParallax) || 0.3;
      window.gsap.to(layer, {
        yPercent: speed * 30,
        ease: 'none',
        scrollTrigger: {
          trigger: layer.closest('section') || layer,
          start: 'top top',
          end: 'bottom top',
          scrub: true,
        },
      });
    });
  }

  /* ---------- 3b. Hero products (reveal + float + scroll parallax) ---------- */
  function setupHeroProducts() {
    const wrap = root.querySelector('[data-ts-hero-products]');
    if (!wrap) return;
    const float = wrap.querySelector('.tsv2-hero__products-float');

    // Fallback: show immediately if GSAP unavailable or reduced-motion
    if (!hasGSAP || prefersReduced) {
      wrap.classList.add('is-ready');
      wrap.style.opacity = '1';
      return;
    }

    // Entrance reveal
    window.gsap.fromTo(
      wrap,
      { opacity: 0, y: 24, scale: 0.97 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.4,
        delay: 0.25,
        ease: 'power3.out',
        onStart: () => wrap.classList.add('is-ready'),
      }
    );

    // Gentle float on inner layer (independent transform, no conflict with outer)
    if (float) {
      window.gsap.to(float, {
        y: -10,
        duration: 4.2,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
    }

    // Scroll-driven parallax on outer wrap (uses yPercent so it composes with entrance y)
    if (hasST) {
      window.gsap.to(wrap, {
        yPercent: -10,
        ease: 'none',
        scrollTrigger: {
          trigger: root.querySelector('.tsv2-hero'),
          start: 'top top',
          end: 'bottom top',
          scrub: 0.6,
        },
      });
    }

    // Desktop only: subtle mouse parallax (cursor gently shifts products)
    if (!isTouch) {
      const hero = root.querySelector('.tsv2-hero');
      const img = wrap.querySelector('img');
      if (!hero || !img) return;
      let rect = null;
      let rafId = null;
      let tx = 0, ty = 0, cx = 0, cy = 0;
      const max = 14;
      const loop = () => {
        cx += (tx - cx) * 0.08;
        cy += (ty - cy) * 0.08;
        img.style.setProperty('--mpx', cx.toFixed(2) + 'px');
        img.style.setProperty('--mpy', cy.toFixed(2) + 'px');
        if (Math.abs(tx - cx) > 0.05 || Math.abs(ty - cy) > 0.05) {
          rafId = requestAnimationFrame(loop);
        } else {
          rafId = null;
        }
      };
      hero.addEventListener('mouseenter', () => { rect = hero.getBoundingClientRect(); });
      hero.addEventListener('mousemove', (e) => {
        if (!rect) rect = hero.getBoundingClientRect();
        const nx = (e.clientX - rect.left) / rect.width - 0.5;
        const ny = (e.clientY - rect.top) / rect.height - 0.5;
        tx = nx * max;
        ty = ny * max;
        if (!rafId) rafId = requestAnimationFrame(loop);
      });
      hero.addEventListener('mouseleave', () => {
        tx = 0; ty = 0;
        if (!rafId) rafId = requestAnimationFrame(loop);
      });
    }
  }

  /* ---------- 4. Animated counters ---------- */
  function setupCounters() {
    const els = root.querySelectorAll('[data-ts-count]');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => (el.textContent = el.dataset.tsCount));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.dataset.tsCount) || 0;
          const isFloat = String(el.dataset.tsCount).indexOf('.') >= 0;
          const duration = 1600;
          const start = performance.now();
          const from = 0;
          function tick(now) {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            const val = from + (target - from) * eased;
            el.textContent = isFloat ? val.toFixed(1) : Math.round(val).toString();
            if (p < 1) requestAnimationFrame(tick);
          }
          requestAnimationFrame(tick);
          io.unobserve(el);
        });
      },
      { threshold: 0.4 }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ---------- 5. Magnetic CTAs ---------- */
  function setupMagnetic() {
    if (prefersReduced || isTouch) return;
    const els = root.querySelectorAll('[data-ts-magnetic]');
    els.forEach((el) => {
      let rect = null;
      let rafId = null;
      let tx = 0, ty = 0, cx = 0, cy = 0;

      const onEnter = () => { rect = el.getBoundingClientRect(); };
      const onMove = (e) => {
        if (!rect) rect = el.getBoundingClientRect();
        const mx = e.clientX - (rect.left + rect.width / 2);
        const my = e.clientY - (rect.top + rect.height / 2);
        tx = mx * 0.25;
        ty = my * 0.35;
        const relX = ((e.clientX - rect.left) / rect.width) * 100;
        const relY = ((e.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty('--mx', relX + '%');
        el.style.setProperty('--my', relY + '%');
        if (!rafId) rafId = requestAnimationFrame(loop);
      };
      const loop = () => {
        cx += (tx - cx) * 0.18;
        cy += (ty - cy) * 0.18;
        el.style.transform = `translate3d(${cx.toFixed(2)}px, ${cy.toFixed(2)}px, 0)`;
        if (Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1) {
          rafId = requestAnimationFrame(loop);
        } else {
          rafId = null;
        }
      };
      const onLeave = () => {
        tx = 0; ty = 0;
        rect = null;
        if (!rafId) rafId = requestAnimationFrame(loop);
      };
      el.addEventListener('mouseenter', onEnter);
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', onLeave);
    });
  }

  /* ---------- 6. Before/After scroll mask ---------- */
  function setupProof() {
    const media = root.querySelector('[data-ts-proof-media]');
    const bar = root.querySelector('.tsv2-proof__bar');
    if (!media) return;

    if (!hasST || prefersReduced) {
      media.style.setProperty('--mask', '50%');
      return;
    }

    const section = root.querySelector('.tsv2-proof');
    window.ScrollTrigger.create({
      trigger: section,
      start: 'top 70%',
      end: 'bottom 30%',
      scrub: 0.6,
      onUpdate: (self) => {
        const p = self.progress;
        const mask = (1 - p) * 92 + 4;
        media.style.setProperty('--mask', mask + '%');
        if (bar) bar.style.setProperty('--p', p.toFixed(3));
      },
    });
  }

  /* ---------- 6b. Engineering callouts (dot <-> feature correspondence) ---------- */
  function setupEngineering() {
    const root2 = root.querySelector('[data-ts-engineering]');
    if (!root2) return;
    const dots = root2.querySelectorAll('[data-ts-callout]');
    const features = root2.querySelectorAll('[data-ts-feature]');
    if (!dots.length) return;

    const setActive = (i) => {
      dots.forEach((d) => d.classList.toggle('is-active', d.dataset.tsCallout === String(i)));
      features.forEach((f) => f.classList.toggle('is-active', f.dataset.tsFeature === String(i)));
    };
    const clearActive = () => {
      dots.forEach((d) => d.classList.remove('is-active'));
      features.forEach((f) => f.classList.remove('is-active'));
    };

    dots.forEach((dot) => {
      const i = dot.dataset.tsCallout;
      dot.addEventListener('mouseenter', () => setActive(i));
      dot.addEventListener('focus', () => setActive(i));
      dot.addEventListener('mouseleave', clearActive);
      dot.addEventListener('blur', clearActive);
      dot.addEventListener('click', () => {
        const target = root2.querySelector(`[data-ts-feature="${i}"]`);
        if (target && isTouch) {
          setActive(i);
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
    features.forEach((feature) => {
      const i = feature.dataset.tsFeature;
      feature.addEventListener('mouseenter', () => setActive(i));
      feature.addEventListener('mouseleave', clearActive);
    });
  }

  /* ---------- 7. Kit horizontal scroll (drag + arrows) ---------- */
  function setupKit() {
    const track = root.querySelector('[data-ts-kit-track]');
    if (!track) return;
    const prev = root.querySelector('[data-ts-kit-prev]');
    const next = root.querySelector('[data-ts-kit-next]');
    const firstCard = track.querySelector('.tsv2-kit-card');
    const step = () => (firstCard ? firstCard.getBoundingClientRect().width + 20 : 360);

    const updateNav = () => {
      if (!prev || !next) return;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    track.addEventListener('scroll', updateNav, { passive: true });
    window.addEventListener('resize', updateNav);
    updateNav();

    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));

    // Drag-to-scroll on desktop (pointer)
    if (isTouch) return;
    let isDown = false, startX = 0, startScroll = 0, moved = false;
    track.addEventListener('pointerdown', (e) => {
      isDown = true; moved = false;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
      track.setPointerCapture(e.pointerId);
    });
    track.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      track.scrollLeft = startScroll - dx;
    });
    const endDrag = (e) => {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      try { track.releasePointerCapture(e.pointerId); } catch (_) {}
    };
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('click', (e) => {
      if (moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);
  }

  /* ---------- 8. Add-to-cart (AJAX) ---------- */
  function setupAddToCart() {
    const cartUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    const buttons = root.querySelectorAll('[data-ts-add-to-cart]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const id = btn.dataset.variantId;
        if (!id || btn.classList.contains('is-loading')) return;
        const label = btn.querySelector('.label');
        const originalText = label ? label.textContent : btn.textContent;
        btn.classList.add('is-loading');
        if (label) label.textContent = 'Adding…';
        try {
          const res = await fetch(cartUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
            },
            body: JSON.stringify({ items: [{ id: parseInt(id, 10), quantity: 1 }] }),
          });
          if (!res.ok) throw new Error('cart add failed');
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          if (label) label.textContent = 'Added ✓';
          // Notify the theme cart drawer if present
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          if (window.CartNotification) {
            try {
              const data = await res.clone().json();
              if (window.CartNotification.renderContents) window.CartNotification.renderContents(data);
            } catch (_) {}
          }
          setTimeout(() => {
            btn.classList.remove('is-added');
            if (label) label.textContent = originalText;
          }, 2200);
        } catch (err) {
          btn.classList.remove('is-loading');
          if (label) label.textContent = 'Try again';
          console.error('[TourSpray] add-to-cart error:', err);
          setTimeout(() => { if (label) label.textContent = originalText; }, 1800);
        }
      });
    });
  }

  /* ---------- 9. Lenis smooth scroll ---------- */
  function setupLenis() {
    if (prefersReduced || isTouch) return;
    if (typeof window.Lenis === 'undefined') return;
    const lenis = new window.Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      smoothTouch: false,
    });
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
    if (hasST) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((t) => lenis.raf(t * 1000));
      window.gsap.ticker.lagSmoothing(0);
    }
    // Anchor link smooth scroll through Lenis
    document.querySelectorAll('a[href^="#"]').forEach((a) => {
      a.addEventListener('click', (e) => {
        const id = a.getAttribute('href');
        if (!id || id === '#') return;
        const target = document.querySelector(id);
        if (target) {
          e.preventDefault();
          lenis.scrollTo(target, { offset: -20 });
        }
      });
    });
  }

  /* ---------- 10. Sticky mobile buy bar ----------
     Shows after user scrolls past the hero, hides when the
     final CTA section enters the viewport (no double-CTA).
     Mobile-only — CSS hides it >= 750px. */
  function setupStickyBuy() {
    const bar = document.querySelector('[data-ts-sticky-buy]');
    if (!bar) return;
    const hero = root.querySelector('.tsv2-hero');
    const finalCta = root.querySelector('.tsv2-final');
    if (!hero || !('IntersectionObserver' in window)) return;

    bar.hidden = false;

    let pastHero = false;
    let finalVisible = false;
    const update = () => {
      if (pastHero && !finalVisible) bar.classList.add('is-shown');
      else bar.classList.remove('is-shown');
    };

    new IntersectionObserver(([entry]) => {
      pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      update();
    }, { threshold: 0, rootMargin: '0px 0px -85% 0px' }).observe(hero);

    if (finalCta) {
      new IntersectionObserver(([entry]) => {
        finalVisible = entry.isIntersecting;
        update();
      }, { threshold: 0.05 }).observe(finalCta);
    }
  }

  /* ---------- Boot ---------- */
  function boot() {
    splitLines();
    setupReveals();
    setupCounters();
    setupMagnetic();
    setupEngineering();
    setupKit();
    setupAddToCart();
    setupLenis();
    setupParallax();
    setupHeroProducts();
    setupProof();
    setupStickyBuy();

    // Keep ScrollTrigger synced with late font loads & image loads
    if (hasST) {
      window.addEventListener('load', () => window.ScrollTrigger.refresh());
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
